import { LoaderFunction } from "react-router-dom";
import { CURRENT_ORDER_QUERY_KEY } from "@/modules/cart/useCartDetails";
import {
  isAnonymousBrowsing,
  isAnonymousCheckout,
  redirectToSignIn,
} from "@/modules/anonymousBrowsing";
import { getPlatform, getWidgets } from "api/methods";
import { STALE_TIME } from "api/staleTimes";
import { prefetchContent } from "modules/routing/contentQueries";
import { Models } from "api/types";
import { queryClient } from "pages/Root";

/**
 * The widget list and the practice settings that decide which widgets are shown. Neither picks the
 * route, so the loader starts them without waiting; the page reads them back through `useQuery`.
 */
export const discoverContentQueries = {
  layout: () => ({ ...getWidgets.keyed({ page: "DiscoverPage" }), staleTime: 1000 * 60 * 2 }),
  platform: () => ({ ...getPlatform.keyed(), staleTime: STALE_TIME.platformConfig }),
} as const;

export const prefetchDiscoverPage = () => prefetchContent(discoverContentQueries);

// A link in a delivered text runs to the next whitespace, so copy the practice typed straight
// after it arrives glued to the id (`?widget=<id>Our`), and a sent text cannot be corrected.
const LEADING_WIDGET_ID = /^[0-9a-f]{8}(?:-[0-9a-f]{4}){3}-[0-9a-f]{12}/i;

const readWidgetId = (query: URLSearchParams) => {
  const widget = query.get("widget");
  return widget?.match(LEADING_WIDGET_ID)?.[0] ?? widget;
};

const load = (request: Request) => {
  /* When displaying the page in a mobile webview, we let the app handle displaying its own navbar */
  const showNavbar = new URL(request.url).searchParams.get("isMobileApp") !== "true";

  const query = new URL(request.url).searchParams;
  const cartDetails =
    queryClient.getQueryData<Models<"ShopDisplayOrderResult">>(CURRENT_ORDER_QUERY_KEY);

  return {
    cartDetails,
    widgetId: readWidgetId(query),
    promotionId: query.get("promotion"),
    showNavbar,
  };
};

export type DiscoverLoaderData = Awaited<ReturnType<typeof load>>;
export type DiscoverContentData = {
  platform: NonNullable<Awaited<ReturnType<typeof getPlatform>>>;
  layout: Awaited<ReturnType<typeof getWidgets>>;
};
export const discoverLoader: LoaderFunction = ({ request }) => {
  const { pathname, search, searchParams } = new URL(request.url);
  const deepLinkNeedsAccount = searchParams.has("promotion") && !isAnonymousCheckout();
  if (isAnonymousBrowsing() && deepLinkNeedsAccount) {
    return redirectToSignIn(pathname + search);
  }
  // Fire-and-forget: neither read picks the route, so nothing waits on them.
  prefetchContent(discoverContentQueries);

  return load(request);
};
